// English (baseline). All keys used across the app should exist here.
// Other languages fall back to English when a key is missing.

const en = {
  // Header / nav
  "nav.simulator": "Simulator",
  "nav.rules": "Rules",
  "brand.tagline": "Income Simulator",

  // Landing hero
  "landing.eyebrow": "Live income simulator",
  "landing.title.pre": "Model your U.CENTER",
  "landing.title.highlight": "commissions",
  "landing.title.post": "in real time.",
  "landing.subtitle": "Interactive projection using the Initial Ripple, Ripple Wave, Deep Ripple and Drop Match rules from u.center/docs. Configure your team, watch the math update instantly.",
  "landing.cta.primary": "Open the simulator",
  "landing.cta.secondary": "See the rules",

  // Simulator page
  "sim.eyebrow": "Live income simulator",
  "sim.title.pre": "Build your team.",
  "sim.title.highlight": "See what you earn.",
  "sim.subtitle": "Build your organisation one level at a time. Each step adds real math — commissions and shares update as soon as you configure the next layer. Rules from",
  "sim.reset": "Reset",
  "sim.share": "Share",

  // Steps
  "step.1.title": "Your starting package",
  "step.1.sub": "Sets your starting rank and unlocks Initial Ripple tiers.",
  "step.1.done": "{pkg} · ${price} · Starts at {rank}",
  "step.2.title": "Your Level 1 recruits",
  "step.2.sub": "The people you personally bring in.",
  "step.2.question": "How many people will you personally recruit?",
  "step.2.avgPkg": "Average package your directs will buy",
  "step.2.done": "{count} directs on {pkg} · ${vol} vol",
  "step.level.title": "Level {n} team",
  "step.level.sub": "Recruits made by each Level {parent} member.",
  "step.level.question": "On average, how many people does each Level {parent} member recruit?",
  "step.level.avgPkg": "Average package for Level {n} members",
  "step.level.done": "{ratio}/Level {parent} member → {count} on {pkg} · ${vol} vol",
  "step.summary.volume": "Level {n} · {count} members",
  "step.summary.volumeShort": "Level 1 volume",
  "step.next": "Next: Level {n} →",
  "step.finish": "Add optional deeper levels →",
  "step.of": "Step {n} of {total}",
  "step.edit": "Edit",

  // Results panel — hero
  "hero.projection": "Projection at your configured depth",
  "hero.youEarn": "You earn",
  "hero.commissionsSub": "in commissions ({pct}% of org volume)",
  "hero.teamGenerates": "Team generates",
  "hero.teamGeneratesSub": "total organisation volume",
  "hero.teamPayout": "Team payout",
  "hero.teamPayoutSub": "commissions paid across your team",
  "hero.plusShares": "plus shares",
  "hero.plusSharesSub": "{start} starting + {earned} via Drop Match",
  "hero.teamSize": "Team size",
  "hero.teamSizeSub": "active members",
  "hero.yourRank": "Your rank",
  "hero.perMember": "$ / team member",
  "hero.perMemberSub": "avg commission earned",
  "hero.avgOrder": "Avg order value",
  "hero.avgOrderSub": "across your team",
  "hero.next": "Next: {rank} · {unlock}",
  "hero.qvToGo": "{qv} QV to go",

  // Empty state
  "empty.title": "Pick your package to begin",
  "empty.sub": "Your projected commissions, shares, and rank will appear here as you build your scenario step by step.",

  // Chart
  "chart.title": "Revenue vs team size",
  "chart.sub": "How commissions grow as your organisation fills up",
  "chart.footer": "{members} members · {vol} vol",

  // Bonus cards
  "bonus.ir.title": "Initial Ripple",
  "bonus.ir.subtitle": "Direct commissions · L1-L5 · {unlocked}/5 unlocked",
  "bonus.ir.footer": "Package unlocks {maxFromPkg}/5. Rank progression unlocks more.",
  "bonus.ir.rowMembers": "{count} members · {vol} vol",
  "bonus.ir.rowLocked": "Locked — need {rank}",
  "bonus.ir.rowPotential": "~{amount} pot.",
  "bonus.rw.title": "Ripple Wave",
  "bonus.rw.subtitle": "R6+ rank commissions · {unlocked}/6 tiers unlocked",
  "bonus.rw.footer": "1% per unlocked tier on every downline order (L1–L11). Max 3 payments per order shared with Initial Ripple.",
  "bonus.rw.rowFires": "Fires on {vol} of downline volume",
  "bonus.rw.rowCapped": "Unlocked — currently capped by 3-payment rule",
  "bonus.rw.rowLocked": "Locked — reach {rank}",
  "bonus.rw.tag": "Unlocks at {rank} · 1% per downline order",
  "bonus.dr.title": "Deep Ripple",
  "bonus.dr.subtitle": "Leadership override · L12-L20 · needs R12+",
  "bonus.dr.g1": "G1 · R12–R14 (0.3/0.6/0.9%)",
  "bonus.dr.g1sub": "Entry leadership",
  "bonus.dr.g2": "G2 · R15–R17 (0.3/0.6/0.9%)",
  "bonus.dr.g2sub": "Advanced leadership",
  "bonus.dr.g3": "G3 · R18–R20 (0.3/0.6/0.9%)",
  "bonus.dr.g3sub": "Elite leadership",
  "bonus.dr.footer": "Rate = position within your group. Fires only when the 3-slot cap has room.",
  "bonus.dm.title": "Drop Match",
  "bonus.dm.subtitle": "Shares (not cash) · L1-L3 · uncapped",
  "bonus.dm.row": "{count} members",
  "bonus.dm.footer": "Drop Match pays U.CENTER shares (points), not USD. Uncapped and separate from the 3-payment rule.",
  "bonus.dm.total": "{shares} shares",

  // Share modal
  "share.eyebrow": "Shareable scenario",
  "share.title": "Send this projection to a prospect",
  "share.desc": "Every input — package, level counts, deeper mode — is baked into the URL. No sign-in, no expiry.",
  "share.linkLabel": "Share link",
  "share.copy": "Copy",
  "share.copied": "Copied",
  "share.footer": "Anyone with this link lands on the exact scenario you built — with all math re-computed live on their device.",
  "share.toast.copied.title": "Link copied",
  "share.toast.copied.desc": "Paste it anywhere — the whole scenario travels with the URL.",
  "share.toast.noPkg.title": "Pick a package first",
  "share.toast.noPkg.desc": "Once you build a scenario, you can share it.",
  "share.toast.loaded.title": "Scenario loaded from link",
  "share.toast.loaded.desc": "Every input has been restored. Feel free to tweak.",
  "share.snapshot.youEarn": "You earn",
  "share.snapshot.teamPayout": "Team payout",
  "share.snapshot.rank": "Rank",

  // Language selector
  "lang.select": "Language",
};

export default en;
